import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useGroups } from '../hooks/useGroups';
import {
  useMaintenanceReports,
  useMaintenanceReportingSettings,
  useUpdateMaintenanceReport,
  useUpdateMaintenanceReportingSettings,
  type MaintenanceReport,
  type MaintenanceReportStatus,
} from '../hooks/useMaintenanceReports';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
  display: grid;
  gap: 1rem;
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 1.8rem;
  color: var(--onSurface);
`;

const HelperText = styled.p`
  color: var(--onSurfaceVariant);
  margin: 0.5rem 0 0;
  font-size: 0.9rem;
`;

const Card = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  padding: 1rem 1.1rem;
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-size: 1.05rem;
  color: var(--onSurface);
`;

const Grid = styled.div`
  display: grid;
  gap: 0.9rem;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
`;

const Field = styled.label`
  display: grid;
  gap: 0.45rem;
  color: var(--onSurface);
  font-size: 0.9rem;
`;

const Input = styled.input`
  border: 1px solid var(--outline);
  border-radius: 10px;
  background: var(--surface);
  color: var(--onSurface);
  padding: 0.7rem 0.85rem;
  font: inherit;
`;

const Select = styled.select`
  border: 1px solid var(--outline);
  border-radius: 10px;
  background: var(--surface);
  color: var(--onSurface);
  padding: 0.7rem 0.85rem;
  font: inherit;
`;

const Textarea = styled.textarea`
  border: 1px solid var(--outline);
  border-radius: 10px;
  background: var(--surface);
  color: var(--onSurface);
  padding: 0.75rem 0.85rem;
  font: inherit;
  min-height: 120px;
  resize: vertical;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
`;

const Button = styled.button`
  border: none;
  border-radius: 9999px;
  padding: 0.72rem 1.1rem;
  background: var(--primary);
  color: var(--onPrimary);
  font: inherit;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  border: 1px solid var(--outline);
  border-radius: 9999px;
  padding: 0.72rem 1.1rem;
  background: var(--surface);
  color: var(--onSurface);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
`;

const FeedbackBanner = styled.p`
  margin: 0;
  padding: 0.75rem 0.9rem;
  border-radius: 10px;
  background: color-mix(in srgb, var(--primary) 12%, transparent);
  color: var(--onSurface);
  font-size: 0.9rem;
`;

const TableCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  overflow: hidden;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--outline);
    text-align: left;
    font-size: 0.9rem;
    color: var(--onSurfaceVariant);
    vertical-align: top;
  }

  th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    background: var(--surfaceVariant);
  }
`;

const ClickableRow = styled.tr`
  cursor: pointer;

  &:hover {
    background: var(--surfaceVariant);
  }
`;

const MessageCell = styled.td`
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusPill = styled.span<{ $status: MaintenanceReportStatus }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  padding: 0.15rem 0.55rem;
  font-size: 0.72rem;
  font-weight: 600;
  background: ${({ $status }) =>
    $status === 'resolved'
      ? 'color-mix(in srgb, #16a34a 18%, transparent)'
      : $status === 'closed'
        ? 'var(--surfaceVariant)'
        : $status === 'in_progress'
          ? 'color-mix(in srgb, #2563eb 18%, transparent)'
          : 'color-mix(in srgb, var(--primary) 18%, transparent)'};
  color: var(--onSurface);
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 40;
  padding: 1.5rem;
`;

const ModalCard = styled.div`
  background: var(--surface);
  border-radius: 16px;
  width: min(760px, 100%);
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.2);
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--outline);
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.1rem;
  color: var(--onSurface);
`;

const ModalCloseButton = styled.button`
  border: none;
  background: transparent;
  color: var(--onSurfaceVariant);
  font-size: 1.15rem;
  cursor: pointer;
`;

const ModalBody = styled.div`
  padding: 1rem 1.25rem 1.25rem;
  display: grid;
  gap: 1rem;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.9rem;
`;

const DetailGroup = styled.div`
  display: grid;
  gap: 0.25rem;
`;

const DetailLabel = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const DetailValue = styled.p`
  margin: 0;
  color: var(--onSurface);
  font-size: 0.95rem;
  white-space: pre-wrap;
  word-break: break-word;
`;

const STATUS_OPTIONS: Array<{ value: MaintenanceReportStatus; label: string }> = [
  { value: 'submitted', label: 'Submitted' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const formatStatusLabel = (status: MaintenanceReportStatus) =>
  STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status;

const formatSourceLabel = (source: MaintenanceReport['source']) => {
  if (source === 'mobile') return 'Mobile app';
  if (source === 'webapp') return 'Webapp';
  return 'Website';
};

const MaintenanceReportsPage: React.FC = () => {
  const { data: reports = [], isLoading, error } = useMaintenanceReports();
  const { data: settings } = useMaintenanceReportingSettings();
  const { data: groups = [], isLoading: groupsLoading } = useGroups();
  const updateReportMutation = useUpdateMaintenanceReport();
  const updateSettingsMutation = useUpdateMaintenanceReportingSettings();

  const [feedback, setFeedback] = useState<string | null>(null);
  const [selectedReport, setSelectedReport] = useState<MaintenanceReport | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<MaintenanceReportStatus>('submitted');
  const [adminNotesDraft, setAdminNotesDraft] = useState('');
  const [settingsGroupId, setSettingsGroupId] = useState('none');

  const groupOptions = useMemo(() => {
    return [...(groups ?? [])].sort((left, right) => left.name.localeCompare(right.name));
  }, [groups]);

  useEffect(() => {
    setSettingsGroupId(settings?.notification_group_id ?? 'none');
  }, [settings]);

  useEffect(() => {
    if (!selectedReport) return;
    setSelectedStatus(selectedReport.status);
    setAdminNotesDraft(selectedReport.admin_notes ?? '');
  }, [selectedReport]);

  useEffect(() => {
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedReport(null);
      }
    };

    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, []);

  const selectedGroupLabel =
    groupOptions.find((group) => group.id === settingsGroupId)?.name ??
    (settingsGroupId === 'none' ? 'None (save to inbox only)' : 'Unknown group');

  const saveSettings = async () => {
    try {
      await updateSettingsMutation.mutateAsync({
        notification_group_id: settingsGroupId === 'none' ? null : settingsGroupId,
      });
      setFeedback('Maintenance reporting settings saved.');
    } catch (saveError) {
      setFeedback(saveError instanceof Error ? saveError.message : 'Could not save maintenance reporting settings.');
    }
  };

  const openReport = (report: MaintenanceReport) => {
    setSelectedReport(report);
    setSelectedStatus(report.status);
    setAdminNotesDraft(report.admin_notes ?? '');
  };

  const saveSelectedReport = async () => {
    if (!selectedReport) return;
    try {
      const updated = await updateReportMutation.mutateAsync({
        id: selectedReport.id,
        status: selectedStatus,
        admin_notes: adminNotesDraft.trim() || null,
      });
      setSelectedReport(updated);
      setFeedback(`Maintenance report "${updated.title}" updated.`);
    } catch (saveError) {
      setFeedback(saveError instanceof Error ? saveError.message : 'Could not update maintenance report.');
    }
  };

  return (
    <Page>
      <div>
        <HeaderTitle>Maintenance Reports</HeaderTitle>
        <HelperText>
          Review maintenance issues sent from the mobile app and choose which group receives each new submission.
        </HelperText>
      </div>

      <Card>
        <SectionTitle>Routing settings</SectionTitle>
        <Grid>
          <Field>
            Notification group
            <Select
              value={settingsGroupId}
              onChange={(event) => setSettingsGroupId(event.target.value)}
              disabled={groupsLoading || updateSettingsMutation.isPending}
            >
              <option value="none">None (save to inbox only)</option>
              {groupOptions.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                  {group.category ? ` (${group.category})` : ''}
                </option>
              ))}
            </Select>
          </Field>
          <Field>
            Current destination
            <Input value={selectedGroupLabel} readOnly />
          </Field>
        </Grid>
        <HelperText>
          New reports are always saved here. When a notification group is selected, the submit flow also creates a post in that group.
        </HelperText>
        <ButtonRow>
          <Button type="button" onClick={saveSettings} disabled={updateSettingsMutation.isPending}>
            {updateSettingsMutation.isPending ? 'Saving...' : 'Save settings'}
          </Button>
        </ButtonRow>
      </Card>

      {feedback ? <FeedbackBanner role="status">{feedback}</FeedbackBanner> : null}

      {isLoading && <HelperText>Loading maintenance reports…</HelperText>}
      {error && (
        <HelperText style={{ color: 'var(--error)' }}>
          Could not load maintenance reports. Make sure the new migration has been applied.
        </HelperText>
      )}

      {!isLoading && reports.length > 0 && (
        <TableCard>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Issue</th>
                  <th>Location</th>
                  <th>Reporter</th>
                  <th>Status</th>
                  <th>Source</th>
                  <th>Description</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => (
                  <ClickableRow
                    key={report.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => openReport(report)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openReport(report);
                      }
                    }}
                  >
                    <td>{report.title}</td>
                    <td>{report.location}</td>
                    <td>{report.reporter_name || '—'}</td>
                    <td>
                      <StatusPill $status={report.status}>{formatStatusLabel(report.status)}</StatusPill>
                    </td>
                    <td>{formatSourceLabel(report.source)}</td>
                    <MessageCell title={report.description}>{report.description}</MessageCell>
                    <td>{report.created_at ? new Date(report.created_at).toLocaleString() : '—'}</td>
                  </ClickableRow>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </TableCard>
      )}

      {!isLoading && !error && reports.length === 0 && (
        <Card>
          <HelperText>No maintenance reports yet.</HelperText>
        </Card>
      )}

      {selectedReport ? (
        <ModalOverlay onClick={() => setSelectedReport(null)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Maintenance Report Details</ModalTitle>
              <ModalCloseButton
                type="button"
                aria-label="Close maintenance report details"
                onClick={() => setSelectedReport(null)}
              >
                ×
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <DetailGrid>
                <DetailGroup>
                  <DetailLabel>Issue</DetailLabel>
                  <DetailValue>{selectedReport.title}</DetailValue>
                </DetailGroup>
                <DetailGroup>
                  <DetailLabel>Location</DetailLabel>
                  <DetailValue>{selectedReport.location}</DetailValue>
                </DetailGroup>
                <DetailGroup>
                  <DetailLabel>Reporter</DetailLabel>
                  <DetailValue>{selectedReport.reporter_name || '—'}</DetailValue>
                </DetailGroup>
                <DetailGroup>
                  <DetailLabel>Email</DetailLabel>
                  <DetailValue>{selectedReport.reporter_email || '—'}</DetailValue>
                </DetailGroup>
                <DetailGroup>
                  <DetailLabel>Phone</DetailLabel>
                  <DetailValue>{selectedReport.reporter_phone || '—'}</DetailValue>
                </DetailGroup>
                <DetailGroup>
                  <DetailLabel>Source</DetailLabel>
                  <DetailValue>{formatSourceLabel(selectedReport.source)}</DetailValue>
                </DetailGroup>
                <DetailGroup>
                  <DetailLabel>Submitted</DetailLabel>
                  <DetailValue>
                    {selectedReport.created_at ? new Date(selectedReport.created_at).toLocaleString() : '—'}
                  </DetailValue>
                </DetailGroup>
                <DetailGroup>
                  <DetailLabel>Group snapshot</DetailLabel>
                  <DetailValue>
                    {groupOptions.find((group) => group.id === selectedReport.notification_group_id)?.name ??
                      (selectedReport.notification_group_id ? selectedReport.notification_group_id : 'None')}
                  </DetailValue>
                </DetailGroup>
              </DetailGrid>

              <DetailGroup>
                <DetailLabel>Description</DetailLabel>
                <DetailValue>{selectedReport.description}</DetailValue>
              </DetailGroup>

              <Grid>
                <Field>
                  Status
                  <Select value={selectedStatus} onChange={(event) => setSelectedStatus(event.target.value as MaintenanceReportStatus)}>
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </Field>
              </Grid>

              <Field>
                Admin notes
                <Textarea
                  value={adminNotesDraft}
                  onChange={(event) => setAdminNotesDraft(event.target.value)}
                  placeholder="Add internal notes, follow-up details, or resolution comments"
                />
              </Field>

              <ButtonRow>
                <Button type="button" onClick={saveSelectedReport} disabled={updateReportMutation.isPending}>
                  {updateReportMutation.isPending ? 'Saving...' : 'Save changes'}
                </Button>
                <SecondaryButton type="button" onClick={() => setSelectedReport(null)}>
                  Close
                </SecondaryButton>
              </ButtonRow>
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      ) : null}
    </Page>
  );
};

export default MaintenanceReportsPage;
